import React, { useState, useEffect, useRef } from 'react';
import { X, GraduationCap, MessageCircle, Mic2, Newspaper, Music, Disc3, Sparkles, Play, Eye, ThumbsUp, Heart, Search, ChevronDown, Youtube, RefreshCw, ExternalLink, ShoppingBag } from 'lucide-react';
import { Button } from './ui/button';
import NewsTab from './DJBooth/NewsTab';
import ForumTab from './DJBooth/ForumTab';
import AcademyTab from './DJBooth/AcademyTab';
import GigBoardTab from './DJBooth/GigBoardTab';
import MarketplaceTab from './DJBooth/MarketplaceTab';
import DJToolsTab from './DJBooth/DJToolsTab';

const DJBoothModal = ({ isOpen, onClose, onShareToDiscord, onViewInMyEvents }) => {
  const [activeTab, setActiveTab] = useState('news');
  const modalContentRef = useRef(null);
  
  // Academy video board state - now managed in AcademyTab component
  const [videoCategories, setVideoCategories] = useState([
    { id: 'all', name: 'All Videos', count: 0 }
  ]);

  // Industry News state - now managed in NewsTab component

  // Forum state - now managed in ForumTab component

  // Scroll to top when tab changes
  useEffect(() => {
    if (modalContentRef.current) {
      modalContentRef.current.scrollTop = 0;
    }
  }, [activeTab]);

  // Listen for event calendar navigation
  useEffect(() => {
    const handleOpenEventCalendar = (event) => {
      const { eventId, eventDate } = event.detail || {};
      console.log('🎛️ DJBoothModal received djBoothOpenEventCalendar, switching to gigs tab');
      // Switch to gigs tab (which has the event calendar)
      setActiveTab('gigs');
      // Give GigBoardTab time to mount, then tell it to open the calendar
      setTimeout(() => {
        console.log('🎛️ DJBoothModal dispatching gigBoardOpenCalendar event');
        window.dispatchEvent(new CustomEvent('gigBoardOpenCalendar', {
          detail: { eventId, eventDate }
        }));
      }, 50);
    };

    window.addEventListener('djBoothOpenEventCalendar', handleOpenEventCalendar);
    return () => window.removeEventListener('djBoothOpenEventCalendar', handleOpenEventCalendar);
  }, []);

  // Listen for event planner deep-link (invite links)
  useEffect(() => {
    const handleOpenEventPlanner = (event) => {
      const { eventId } = event.detail || {};
      setActiveTab('gigs');
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('gigBoardOpenPlanner', { detail: { eventId } }));
      }, 50);
    };
    window.addEventListener('djBoothOpenEventPlanner', handleOpenEventPlanner);
    return () => window.removeEventListener('djBoothOpenEventPlanner', handleOpenEventPlanner);
  }, []);

  // Listen for tab switch events from feature cards
  useEffect(() => {
    const validTabs = ['news', 'forum', 'gigs', 'marketplace', 'tools', 'academy'];
    const handleSetTab = (event) => {
      if (event.detail && validTabs.includes(event.detail)) {
        setActiveTab(event.detail);
      }
    };

    window.addEventListener('djBoothSetTab', handleSetTab);
    return () => window.removeEventListener('djBoothSetTab', handleSetTab);
  }, []);

  // News tab now self-manages loading via NewsTab component
  
  // Toggle body class while DJ Booth is open — used by mobile CSS to hide
  // bottom nav + attribution badges so they don't cover modal content
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('dj-booth-modal-open');
    } else {
      document.body.classList.remove('dj-booth-modal-open');
    }
    return () => document.body.classList.remove('dj-booth-modal-open');
  }, [isOpen]);

  // Forum tab now self-manages loading via ForumTab component

  // Forum categories now loaded in ForumTab component

  // Academy tab now self-manages loading via AcademyTab component

  // Load videos from API

  // News Functions
  // News functions now in NewsTab component

  const getTimeAgo = (dateString) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const seconds = Math.floor((now - date) / 1000);
      
      if (seconds < 60) return 'Just now';
      if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
      if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
      if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
      if (seconds < 2592000) return `${Math.floor(seconds / 604800)}w ago`;
      return `${Math.floor(seconds / 2592000)}mo ago`;
    } catch {
      return 'Recently';
    }
  };


  // Helper functions
  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toString() || '0';
  };


  if (!isOpen) return null;

  const tabs = [
    { id: 'news', label: 'Industry News', icon: Newspaper },
    { id: 'forum', label: 'Forum', icon: MessageCircle },
    { id: 'gigs', label: 'Gig & Event Hub', icon: Mic2 },
    { id: 'marketplace', label: 'Marketplace & Directory', icon: ShoppingBag },
    { id: 'tools', label: 'DJ Tools', icon: Music },
    { id: 'academy', label: 'The Academy', icon: GraduationCap }
  ];

  const renderTabContent = () => {
    // Industry News tab - using NewsTab component
    if (activeTab === 'news') {
      return <NewsTab />;
    }

    // Forum tab - using ForumTab component
    if (activeTab === 'forum') {
      return <ForumTab />;
    }

    // Academy tab - using AcademyTab component
    if (activeTab === 'academy') {
      return <AcademyTab />;
    }

    // Gig Board tab - using GigBoardTab component
    if (activeTab === 'gigs') {
      return <GigBoardTab onViewInMyEvents={onViewInMyEvents} onDiscordShare={onShareToDiscord} />;
    }

    // Marketplace tab - using MarketplaceTab component
    if (activeTab === 'marketplace') {
      return <MarketplaceTab onShareToDiscord={onShareToDiscord} />;
    }

    // DJ Tools tab - using DJToolsTab component
    if (activeTab === 'tools') {
      return <DJToolsTab />;
    }

    // Other tabs - render empty state
    const tabContent = {
      news: {
        title: 'Industry News',
        icon: Newspaper,
        description: 'Stay updated with the latest DJ industry news and trends',
        comingSoon: 'Industry news and updates coming soon!'
      },
      forum: {
        title: 'Community Forum',
        icon: MessageCircle,
        description: 'Connect with DJs, share tips, and discuss techniques',
        comingSoon: 'Discussion forums and community Q&A coming soon!'
      },
      gigs: {
        title: 'Gig & Event Hub',
        icon: Mic2,
        description: 'Find DJ gigs, post opportunities, and connect with event organizers',
        comingSoon: 'Gig postings and job marketplace coming soon!'
      },
      tools: {
        title: 'DJ Tools',
        icon: Music,
        description: 'BPM analyzer, key finder, setlist builder, and more',
        comingSoon: 'Professional DJ tools coming soon!'
      }
    };

    const content = tabContent[activeTab];
    const IconComponent = content.icon;

    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        textAlign: 'center',
        padding: '2rem'
      }}>
        <h2 style={{
          fontSize: '2.5rem',
          background: 'linear-gradient(135deg, #00ffff, #ff00ff)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          marginBottom: '1rem',
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          justifyContent: 'center'
        }}>
          <IconComponent size={48} style={{ color: '#00ffff' }} />
          {content.title}
        </h2>
        <p style={{
          fontSize: '1.2rem',
          color: 'rgba(255, 255, 255, 0.7)',
          marginBottom: '2rem',
          maxWidth: '600px'
        }}>
          {content.description}
        </p>
        <p style={{
          fontSize: '1rem',
          color: 'rgba(0, 255, 255, 0.8)',
          fontWeight: '600',
          padding: '1rem 2rem',
          background: 'rgba(0, 255, 255, 0.1)',
          borderRadius: '12px',
          border: '1px solid rgba(0, 255, 255, 0.3)'
        }}>
          {content.comingSoon}
        </p>
      </div>
    );
  };

  return (
    <div 
      className="modal-overlay"
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
    >
      <div 
        className="dj-booth-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '90vw',
          maxWidth: '1400px',
          maxHeight: '90vh',
          background: 'rgba(26, 26, 46, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: '20px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          border: '5px solid transparent',
          backgroundImage: `
            linear-gradient(rgba(26, 26, 46, 0.95), rgba(26, 26, 46, 0.95)),
            linear-gradient(135deg, #00ffff, #ff00ff, #00ffff)
          `,
          backgroundOrigin: 'border-box',
          backgroundClip: 'padding-box, border-box',
          animation: 'borderFlow 3s linear infinite'
        }}
      >
        {/* Modal Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '1.5rem 2rem',
          borderBottom: '2px solid rgba(0, 255, 255, 0.2)',
          background: 'rgba(0, 0, 0, 0.3)'
        }}>
          <h2 style={{
            fontSize: '2rem',
            fontWeight: 'bold',
            background: 'linear-gradient(135deg, #00ffff, #ff00ff)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <Disc3 size={32} style={{ color: '#00ffff' }} />
            The DJ Booth
          </h2>
          
          <Button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            style={{
              background: 'transparent',
              border: '2px solid rgba(0, 255, 255, 0.5)',
              color: '#00ffff',
              padding: '0.5rem',
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s ease',
              width: '40px',
              height: '40px',
              zIndex: 10
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(0, 255, 255, 0.1)';
              e.currentTarget.style.borderColor = '#00ffff';
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderColor = 'rgba(0, 255, 255, 0.5)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <X size={20} />
          </Button>
        </div>

        {/* Tab Navigation */}
        <div className="djbooth-tab-strip" style={{
          display: 'flex',
          gap: '0.5rem',
          padding: '0 1.5rem',
          borderBottom: '2px solid rgba(0, 255, 255, 0.2)',
          background: 'rgba(0, 0, 0, 0.2)',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch'
        }}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '1rem 1.5rem',
                background: activeTab === tab.id ? 'rgba(0, 255, 255, 0.1)' : 'transparent',
                border: 'none',
                borderBottom: activeTab === tab.id ? '3px solid #00ffff' : '3px solid transparent',
                color: activeTab === tab.id ? '#00ffff' : 'rgba(255, 255, 255, 0.7)',
                fontWeight: '600',
                fontSize: '0.95rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                textShadow: activeTab === tab.id ? '0 0 10px rgba(0, 255, 255, 0.8)' : 'none'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== tab.id) {
                  e.currentTarget.style.color = 'rgba(0, 255, 255, 0.8)';
                  e.currentTarget.style.background = 'rgba(0, 255, 255, 0.05)';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== tab.id) {
                  e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)';
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              <tab.icon size={18} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Modal Content Area */}
        <div 
          ref={modalContentRef}
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '2rem'
          }}
        >
          {renderTabContent()}
        </div>
      </div>

      <style>{`
        @keyframes borderFlow {
          0%, 100% { 
            background-position: 0% 50%; 
          }
          50% { 
            background-position: 100% 50%; 
          }
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .dj-booth-modal {
          background-size: 100% 100%, 200% 200%;
        }

        /* Custom scrollbar for modal content */
        .dj-booth-modal > div:last-child::-webkit-scrollbar {
          width: 8px;
        }

        .dj-booth-modal > div:last-child::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.3);
          border-radius: 10px;
        }

        .dj-booth-modal > div:last-child::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #00ffff, #ff00ff);
          border-radius: 10px;
        }

        .dj-booth-modal > div:last-child::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(135deg, #00ffff, #00ffff);
        }

        /* Tab navigation scrollbar (for mobile) */
        .dj-booth-modal > div:nth-child(2)::-webkit-scrollbar {
          height: 4px;
        }

        .dj-booth-modal > div:nth-child(2)::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.3);
        }

        .dj-booth-modal > div:nth-child(2)::-webkit-scrollbar-thumb {
          background: rgba(0, 255, 255, 0.5);
          border-radius: 10px;
        }
      `}</style>
    </div>
  );
};

export default DJBoothModal;
